import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { Col, ListGroup, Badge, OverlayTrigger, Tooltip, Spinner, Alert } from 'react-bootstrap';
import { Line } from 'react-chartjs-2';
import moment from 'moment';
import { getCurrencySymbol } from '../../common_components/choices';
import 'chart.js/auto';
import ProductPriceModal from './ProductPriceModal';

const ProductPrice = ({ brand, category, product, clearBrandFilter, clearCategoryFilter, clearProductFilter }) => {
    const [existingPrices, setExistingPrices] = useState([]);
    const [nextPricesUrl, setNextPricesUrl] = useState(null);
    const [showPriceModal, setShowPriceModal] = useState(false);
    const [selectedPrice, setSelectedPrice] = useState(null);
    const [loading, setLoading] = useState(false); // Loading state
    const listGroupRef = useRef(null);

    const token = localStorage.getItem('token');

    const pageSize = 10;

    const fetchProductPrices = useCallback(async (nextUrl = null, brandId = null, categorySlug = null, productId = null) => {
        setLoading(true);
        const params = { page_size: pageSize };
        if (brandId) {
            params.product_brand_id = brandId;
        }
        if (categorySlug) {
            params.product_category_slug = categorySlug;
        }
        if (productId) {
            params.product_id = productId;
        }

        const url = nextUrl || `${process.env.REACT_APP_BACKEND_API_URL}/highgarden/grocery-product-prices/`;
        try {
            const response = await axios.get(url, {
                params,
                headers: {
                    'Authorization': `Token ${token}`
                },
            });

            if (nextUrl) {
                setExistingPrices(prevPrices => [...prevPrices, ...response.data.results]);
            } else {
                setExistingPrices(response.data.results);
            }
            setNextPricesUrl(response.data.next);
        } catch (error) {
            console.error('Error fetching product prices:', error);
        } finally {
            setLoading(false);
        }
    }, [token]);

    useEffect(() => {
        if (product) {
            setExistingPrices([]); // Reset prices list on product change
            setNextPricesUrl(null); // Reset pagination URL on product change
            fetchProductPrices(null, brand ? brand.id : null, category ? category.slug : null, product.id);
        }
    }, [brand, category, product, fetchProductPrices]);

    useEffect(() => {
        const handleScroll = () => {
            if (listGroupRef.current) {
                const { scrollTop, scrollHeight, clientHeight } = listGroupRef.current;
                if (scrollTop + clientHeight >= scrollHeight - 1 && nextPricesUrl && !loading) {
                    fetchProductPrices(nextPricesUrl, brand ? brand.id : null, category ? category.slug : null, product ? product.id : null);
                }
            }
        };

        const listGroupElement = listGroupRef.current;
        if (listGroupElement) {
            listGroupElement.addEventListener('scroll', handleScroll);
        }
        return () => {
            if (listGroupElement) {
                listGroupElement.removeEventListener('scroll', handleScroll);
            }
        };
    }, [nextPricesUrl, brand, category, product, loading, fetchProductPrices]);

    const handleShowProductPriceModal = (productPrice = null) => {
        setSelectedPrice(productPrice);
        setShowPriceModal(true);
    };

    const refreshPrices = () => {
        setExistingPrices([]); // Reset prices list
        setNextPricesUrl(null); // Reset pagination URL
        fetchProductPrices(null, brand ? brand.id : null, category ? category.slug : null, product.id);
    };

    // Using shared `getItemMeasure` from services.js

    const sortedPrices = existingPrices.slice().sort((a, b) => new Date(a.date_time) - new Date(b.date_time));
    const minPrice = Math.min(...sortedPrices.map(price => price.price));
    const yMinValue = minPrice * 0.9;

    const chartData = {
        labels: sortedPrices.map(price => moment(price.date_time).format('DD-MM-YYYY')),
        datasets: [
            {
                label: 'Price',
                data: sortedPrices.map(price => price.price),
                borderColor: 'rgba(75, 192, 192, 1)',
                backgroundColor: 'rgba(75, 192, 192, 0.2)',
                fill: true,
            },
        ],
    };

    const chartOptions = {
        scales: {
            x: {
                title: {
                    display: true,
                    text: 'Date',
                },
            },
            y: {
                title: {
                    display: true,
                    text: `Price (${getCurrencySymbol(existingPrices[0]?.price_currency)})`, // Adding currency symbol to the Y-axis label
                },
                beginAtZero: true,
                min: yMinValue,
            },
        },
    };

    return (
        <Col>
            <h5>
                {brand && (
                    <span>
                        Brand: {brand.name}
                        {brand &&
                            <OverlayTrigger
                                placement="top"
                                overlay={<Tooltip id="tooltip-clear-brand">
                                    Clear filter
                                </Tooltip>}>
                                <i className="bi bi-x-circle"
                                    style={{ cursor: 'pointer', marginLeft: '8px', position: 'relative', top: '2px' }}
                                    onClick={clearBrandFilter}>
                                </i>
                            </OverlayTrigger>}
                    </span>
                )}
                {category && (
                    <span>
                        Category: {category.name}
                        {category &&
                            <OverlayTrigger
                                placement="top"
                                overlay={<Tooltip id="tooltip-clear-category">
                                    Clear filter
                                </Tooltip>}>
                                <i className="bi bi-x-circle"
                                    style={{ cursor: 'pointer', marginLeft: '8px', position: 'relative', top: '2px' }}
                                    onClick={clearCategoryFilter}>
                                </i>
                            </OverlayTrigger>}
                    </span>
                )}
                {product && (
                    <span>
                        Product: {product.name}
                        {product &&
                            <OverlayTrigger
                                placement="top"
                                overlay={<Tooltip id="tooltip-clear-product">
                                    Clear filter
                                </Tooltip>}>
                                <i className="bi bi-x-circle"
                                    style={{ cursor: 'pointer', marginLeft: '8px', position: 'relative', top: '2px' }}
                                    onClick={clearProductFilter}>
                                </i>
                            </OverlayTrigger>}
                    </span>
                )}
            </h5>
            {!product ? (
                <Alert variant="warning">
                    Please select a product to view prices.
                </Alert>
            ) : (
                <>
                    {existingPrices.length === 0 ? (
                        <Alert variant="warning">
                            No prices available for the selected product.
                        </Alert>
                    ) : (
                        <>
                            <Line data={chartData} options={chartOptions} />
                            <ListGroup ref={listGroupRef} className="hidden-scrollbar" style={{ maxHeight: '400px', overflowY: 'auto', marginTop: '20px' }}>
                                {existingPrices.map(product_price => (
                                    <OverlayTrigger
                                        placement="top"
                                        overlay={<Tooltip id="tooltip-owner">
                                            {product_price.store && product_price.store.name}
                                        </Tooltip>}
                                    >
                                        <ListGroup.Item
                                            key={product_price.id}
                                            onClick={() => handleShowProductPriceModal(product_price)}
                                            style={{ cursor: 'pointer' }}
                                        >
                                            <div className="d-flex justify-content-between w-100">
                                                <div>
                                                    <OverlayTrigger
                                                        placement="top"
                                                        overlay={<Tooltip id="tooltip-owner">
                                                            {product_price.date_time && moment(product_price.date_time).format('DD-MM-YYYY HH:mm a')}
                                                        </Tooltip>}
                                                    >
                                                        <Badge style={{ marginRight: '8px' }}>{product_price.date_time ? moment(product_price.date_time).format('DD-MM-YYYY') : null}</Badge>
                                                    </OverlayTrigger>
                                                    {product_price.price ? `${product_price.price} ${getCurrencySymbol(product_price.price_currency)}` : null}

                                                    {product && product.description && (
                                                        <OverlayTrigger
                                                            overlay={<Tooltip id="tooltip-owner">
                                                                {product.description ? product.description : 'No description'}
                                                            </Tooltip>}
                                                        >
                                                            <i className="bi bi-info-circle" style={{ marginLeft: '8px' }}></i>
                                                        </OverlayTrigger>
                                                    )}
                                                </div>
                                                <div>
                                                    {product && product_price.source ? (<Badge className='bg-danger'>{product_price.source}</Badge>) : null}
                                                    {product_price.is_owner ?
                                                        <OverlayTrigger
                                                            placement="top"
                                                            overlay={<Tooltip id="tooltip-owner">
                                                                Price added by me
                                                            </Tooltip>}
                                                        >
                                                            <i className="bi bi-person-fill" style={{ marginLeft: '8px' }}></i>
                                                        </OverlayTrigger>
                                                        : null}
                                                </div>
                                            </div>
                                        </ListGroup.Item>
                                    </OverlayTrigger>
                                ))}
                            </ListGroup>
                        </>
                    )}
                </>
            )}
            {loading && (
                <div className="d-flex justify-content-center mt-2">
                    <Spinner animation="border" />
                </div>
            )}
            <ProductPriceModal
                show={showPriceModal}
                handleClose={() => setShowPriceModal(false)}
                productPrice={selectedPrice}
                product={product}
                refreshProductPrices={refreshPrices}
            />
        </Col>
    );
};

export default ProductPrice;
